import { AST } from '../../../ast';
import { CompileOutput, ParseRes } from '../../eraql-ast/ast';
import { c, ParseFunc } from '../combinators';
import { SpecialForm } from '../types';
import { tryTypeCheckExpr } from './helpers';
import _ from 'lodash';

const winOp =
  (op: AST.WindowOp, arity: number): SpecialForm =>
  (ast, ctx, func) => {
    const numLitOrUnbounded: ParseFunc<number | 'unbounded'> = (ast, ctx) => {
      const unbounded = c.EnumLit('unbounded')(ast, ctx);
      if (unbounded.isOk()) {
        return unbounded;
      }
      return c.NumLit(ast, ctx);
    };

    const args = c.ArgsWithOpts(
      Array(arity).fill(c.Expr) as (typeof c.Expr)[],
      c.StructOf({
        required: {},
        optional: {
          partition_by: c.ListOf(c.Expr),
          order_by: c.ListOf(c.OrderBy),
          frame: c.StructOf({
            required: {},
            optional: {
              preceding: numLitOrUnbounded,
              following: numLitOrUnbounded,
            },
            strict: true,
          }),
        },
        strict: true,
      })
    )(ast, ctx, func);

    return args.andThen(([args, opts = {}]): ParseRes<CompileOutput> => {
      const frame = opts.frame
        ? {
            preceding: opts.frame?.v[0].preceding?.v[0] ?? null,
            following: opts.frame?.v[0].following?.v[0] ?? null,
          }
        : null;

      const orderBy = opts.order_by
        ? opts.order_by.v[0].map(([{ expr, direction }, _meta]) => ({
            direction,
            expr: expr.ast,
          }))
        : [];

      const partitionBy = opts.partition_by
        ? opts.partition_by.v[0].map(([{ ast }, _meta]) => ast)
        : [];

      const wf: AST._Window<AST.ExprMacroChildren> = {
        t: 'window',
        op,
        args: args.map((arg) => arg.ast),
        frame,
        over: { orderBy, partitionBy },
      };

      return tryTypeCheckExpr(wf, func[1]);
    });
  };

const WINDOWS: Record<
  `${AST.WindowOp}_over`,
  [arity: number, op: AST.WindowOp]
> = {
  min_over: [1, 'min'],
  max_over: [1, 'max'],
  string_agg_over: [1, 'string_agg'],
  rank_over: [0, 'rank'],
  row_number_over: [0, 'row_number'],
  dense_rank_over: [0, 'dense_rank'],
  percent_rank_over: [0, 'percent_rank'],
  lead_over: [1, 'lead'],
  lag_over: [1, 'lag'],
  sum_over: [1, 'sum'],
  avg_over: [1, 'avg'],
  count_over: [1, 'count'],
  first_value_over: [1, 'first_value'],
  last_value_over: [1, 'last_value'],
  ntile_over: [1, 'ntile'],
  // todo
  corr_over: [1, 'corr'],
};

export const WINDOW_SPECIAL_FORMS: Record<
  `${AST.WindowOp}_over`,
  readonly [artiy: number | number[] | 'any', fn: SpecialForm]
> = _.mapValues(
  WINDOWS,
  ([arity, op]) => [arity + 1, winOp(op, arity)] as const
);
